import { useConfirm } from "material-ui-confirm";
import { AugmentedShipment } from "../../../components/trip-planning/PlanningTool/PlanningTool";
import {
  useAddTripMutation,
  useRemoveShipmentFromTripMutation,
} from "../../../graphql/generated";
import TripSeparationTool from "./TripSeparationTool";
import { useTranslation } from "react-i18next";

type TripSeparationToolProps = {
  shipments: AugmentedShipment[];
  onRefresh: (shipmentIds: string[]) => Promise<void>;
  onClose: () => void;
  onShipmentsUncombine?: (shipments: AugmentedShipment[]) => void;
};

export default function TripSeparationToolContainer({
  shipments,
  onRefresh,
  onClose,
  onShipmentsUncombine,
}: TripSeparationToolProps) {
  const removeShipmentFromTripMutation = useRemoveShipmentFromTripMutation();
  const addTripMutation = useAddTripMutation();
  const { t } = useTranslation(["trips", "common"]);
  const confirm = useConfirm();
  return (
    <TripSeparationTool
      onSubmit={async (removedShipments, tripId) => {
        const isUnsavedTrip = shipments[0]?.trip?.tripNumber === "NEW";

        if (isUnsavedTrip && onShipmentsUncombine) {
          const shipmentsToSeparate = shipments.filter((s) =>
            removedShipments.includes(s._id),
          );
          onShipmentsUncombine(shipmentsToSeparate);
          onClose();
          return;
        }

        try {
          await removeShipmentFromTripMutation.mutateAsync({
            shipmentIds: removedShipments,
            tripId,
          });
          await addTripMutation.mutateAsync({
            newTripData: {
              shipments: removedShipments,
            },
          });
          confirm({
            title: t("common:success", "Success"),
            description: t(
              "trips:tripsSeparationSuccess",
              "Trip for separated shipments created successfully"
            ),
            hideCancelButton: true,
          });
          await onRefresh(shipments.map((s) => s._id));
          onClose();
        } catch (error) {
          confirm({
            title: "Error",
            description: (error as Error | null)?.message || "Unknown error",
            titleProps: { color: "error" },
            hideCancelButton: true,
          });
        }
      }}
      shipments={shipments}
      isLoading={
        removeShipmentFromTripMutation.isLoading || addTripMutation.isLoading
      }
    />
  );
}
